import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Inject,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from "@angular/material/legacy-dialog";
import { Store } from "@ngrx/store";
import { ObgConfigFeAdaptiveClientCommon, ProductTheme } from "@obg/common.config";
import { GtmDataLayerVar } from "@obg/gtm.types/adaptive";
import { DialogLayoutComponent, NavbarOptions } from "@obg/mat.ui.core";
import { CommonState, isEmailValid } from "@obg/ngx.common";
import { DeviceConfigService, ConfigService } from "@obg/ngx.global";
import { TelemetryService } from "@obg/ngx.global.evo";
import { GtmEventAction } from "@obg/ngx.gtm/adaptive";
import { OBG_VALIDATORS } from "@obg/ngx.ui.common";
import { merge, Observable, Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";

import { LoginAction, LoginSelector, ResetPasswordModel } from "../sdk";

import { renewPasswordGtmEvents, renewPasswordGtmTags } from "./renew-password.const";
import { RenewPasswordContainerData } from "./renew-password.model";

const USERNAME_CONTROL_NAME = "username";

@Component({
	selector: "obg-m-renew-password",
	styleUrls: ["./renew-password.container.scss"],
	templateUrl: "./renew-password.container.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class RenewPasswordContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-renew-password") hostCssClass = true;
	@HostBinding("attr.test-id") testId = "obg-m-renew-password-modal";
	@ViewChild("dialog") dialogLayout: DialogLayoutComponent | undefined;

	@Output() success = new EventEmitter();

	formGroup: UntypedFormGroup;
	isBusy = false;
	isFormSubmitted = false;
	isDesktop = this.deviceConfigService.isDesktop();
	navbarOptions: NavbarOptions = {
		hideBackButton: false,
		showCloseButton: this.isDesktop,
		preventDefaultBack: true,
	};

	hasSucceeded$: Observable<boolean> | undefined;
	hasFailed$: Observable<boolean> | undefined;

	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CommonState>,
		private formBuilder: UntypedFormBuilder,
		private loginAction: LoginAction,
		private telemetryService: TelemetryService,
		private deviceConfigService: DeviceConfigService,
		@Inject(MAT_DIALOG_DATA) public data: RenewPasswordContainerData | undefined,
		private configService: ConfigService<ObgConfigFeAdaptiveClientCommon>,
		private changeDetector: ChangeDetectorRef
	) {
		this.formGroup = this.formBuilder.group({
			[USERNAME_CONTROL_NAME]: this.formBuilder.control("", [Validators.required, OBG_VALIDATORS.email]),
		});
	}

	ngOnInit() {
		const productTheme = this.configService.get((config) => config.common.productTheme, ProductTheme.Light);

		this.telemetryService.track({
			[GtmDataLayerVar.VirtualUrl]: renewPasswordGtmTags.virtualURL,
			[GtmDataLayerVar.VirtualTitle]: renewPasswordGtmTags.virtualTitle,
			[GtmDataLayerVar.Lobby]: renewPasswordGtmTags.lobby,
			[GtmDataLayerVar.LobbyName]: renewPasswordGtmTags.lobbyName,
			[GtmDataLayerVar.SubArea]: renewPasswordGtmTags.subArea,
			[GtmDataLayerVar.Product_Theme]: productTheme,
			event: renewPasswordGtmEvents.virtualPageView,
		});

		if (!!this.data?.selectedUserEmail && isEmailValid(this.data?.selectedUserEmail)) {
			const usernameControl = this.formGroup.get(USERNAME_CONTROL_NAME);
			usernameControl?.setValue(this.data.selectedUserEmail);
			usernameControl?.disable();
			usernameControl?.markAsTouched();
		}

		this.subscribeToData();
	}

	private subscribeToData() {
		this.hasSucceeded$ = this.store.select(LoginSelector.isForgotPasswordByEmailSuccess).pipe(
			filter(Boolean),
			tap(() => {
				this.isBusy = false;
				this.telemetryService.track({
					event: GtmEventAction.ForgottenPasswordSuccess,
				});
				this.success.emit();
			})
		);

		this.hasFailed$ = this.store.select(LoginSelector.isForgotPasswordByEmailFailed).pipe(
			filter(Boolean),
			tap(() => {
				this.isBusy = false;
			})
		);

		this.data$$ = merge(this.hasSucceeded$, this.hasFailed$).subscribe(() => this.changeDetector.markForCheck());
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	submit() {
		const data: ResetPasswordModel = this.formGroup.getRawValue();

		if (this.formGroup.invalid) {
			return;
		}

		this.telemetryService.track({
			event: GtmEventAction.LoginPasswordFormSubmit,
		});

		this.isBusy = true;
		this.isFormSubmitted = true;

		this.store.dispatch(this.loginAction.resetPasswordByEmail(data.username));
	}
}
